import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import type { Control, UseFormRegister } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { SidepanelTitle } from '../components/SidepanelTitle';
import { TitleSkeleton } from '../components/SkeletonLoaders';
import { CommentableTitle } from './comments/CommentableTitle';
import type { ProfileForm } from './types';

type JobProfileTitleProps = {
    readonly activeLanguageIndex: number;
    readonly architectureId: string;
    readonly control: Control<ProfileForm>;
    readonly isCommentable: boolean;
    readonly isEditing: boolean;
    readonly isGeneratingTranslations: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly register: UseFormRegister<ProfileForm>;
    readonly selectedVersion: string | undefined;
    readonly onInput: () => void;
};

export const JobProfileTitle: FunctionComponent<JobProfileTitleProps> = ({
    activeLanguageIndex,
    architectureId,
    control,
    isCommentable,
    isEditing,
    isGeneratingTranslations,
    itemId,
    itemType,
    onInput,
    register,
    selectedVersion,
}) => {
    const watchedTitle = useWatch({ control, name: `translations.${activeLanguageIndex}.title` });
    const title = watchedTitle ?? '';

    if (isGeneratingTranslations && is.falsy(watchedTitle)) {
        return <TitleSkeleton />;
    }

    const sidepanelTitle = (
        <SidepanelTitle
            key={activeLanguageIndex}
            architectureId={architectureId}
            isEditing={isEditing}
            itemId={itemId}
            itemType={itemType}
            register={register(`translations.${activeLanguageIndex}.title`)}
            title={title}
            version={selectedVersion}
            onInput={onInput}
        />
    );

    return isCommentable ? (
        <CommentableTitle itemId={itemId} newThreadCreationDisabled={false} title={title}>
            {sidepanelTitle}
        </CommentableTitle>
    ) : (
        sidepanelTitle
    );
};
